import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { idbCache } from './idb';

/**
 * 先给上次的数据,再去拿新的。
 *
 * ★ 用户原话:「每次打开新的页面都要重新加载数据然后有了数据后才显示,
 *   能不能缓存到浏览器,然后默认显示缓存的数据,联网的数据异步加载,
 *   加载完成后再刷新数据」。
 *
 * ★ 司机端**特意没有**做这件事(见 driver/lib/cache.ts 顶部:
 *   「任务状态变了界面却还显示旧的,比多等 200ms 危险得多」)。
 *   管理端可以不一样,理由有两条,缺一条都不该这么做:
 *     · 拿旧数据去操作会被**乐观锁**挡住(transitions 带 expectedVersion),
 *       表现是「请刷新后重试」,而不是静默覆盖掉别人的改动
 *     · 界面上一直挂着「更新中」,人知道眼前这份可能是旧的
 *   订单**详情**不用这个 hook —— 打开详情就是为了对这一单动手,
 *   在那里省 200ms 不划算。
 *
 * 两层缓存,各解决一件事:
 *   · 内存 —— 切菜单回来立刻出(同一次会话内)
 *   · IndexedDB —— 刚打开浏览器就有东西看(跨刷新)
 */

interface Entry<T> {
  id: string;
  cachedAt: number;
  payload: T;
}

/**
 * 同一次会话内切来切去,不该每次都读一遍 IndexedDB。
 *
 * ★ **必须有上限**。
 *
 *   第一版没有,而 key 是带查询串的 —— 订单列表每改一次筛选、
 *   地图每拖动一次都是一个新 key,每条装着一整份响应
 *   (地图那份最多两千个点)。于是它只增不减:
 *   拖几十次地图,几十份数据留在内存里永不释放,
 *   而 patchCachedOrder 每次状态变更还要把它整个遍历一遍。
 *
 *   用户报的「管理端和手机端会突然卡住,整个浏览器程序都卡住,
 *   标签也无法切换」就是这么来的 —— 那是主线程被内存和 GC 拖死的样子,
 *   而不是某个请求慢。
 *
 * ★ Map 保持插入顺序,所以读命中时挪到末尾、超限时删最前面的,
 *   就是一个 LRU。
 */
const MEM_MAX = 24;
const mem = new Map<string, Entry<unknown>>();

/** 放进内存缓存,顺便把最久没碰过的挤出去 */
function remember(key: string, entry: Entry<unknown>): void {
  if (mem.has(key)) mem.delete(key);
  mem.set(key, entry);
  while (mem.size > MEM_MAX) {
    const oldest = mem.keys().next().value;
    if (oldest === undefined) break;
    mem.delete(oldest);
  }
}

/** 读:命中就挪到末尾(LRU) */
function recall<T>(key: string): Entry<T> | undefined {
  const hit = mem.get(key) as Entry<T> | undefined;
  if (hit) {
    mem.delete(key);
    mem.set(key, hit);
  }
  return hit;
}

/**
 * 超过这个时长的缓存不再拿来显示。
 *
 * ★ 隔夜的订单列表基本全是错的 —— 宁可让人等一下,
 *   也不要给他一屏昨天的单然后指望他注意到那行「更新中」。
 */
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

const KEY = (path: string): string => `admin:${path}`;

/**
 * 缓存结构的版本 —— 变了就把旧的整个清掉。
 *
 * ★ 这不是洁癖,是**修上一版留下的烂摊子**:
 *   第一版的 key 里带着精确 bbox,用户每拖动一次地图就在 IndexedDB 里
 *   多存一份最多两千个点的数据,而且永远不删。光改代码救不了
 *   已经装了一肚子垃圾的浏览器 —— 那些条目还在,还会继续拖慢它。
 *
 * ★ 版本号往上加一次,所有人下次打开自动清空重来。
 *   代价只是一次「第一屏要等一下」,而收益是那台手机不再卡死。
 */
const SCHEMA = 'v2';
const SCHEMA_KEY = 'delivery.admin.cacheSchema';

void (async () => {
  try {
    if (localStorage.getItem(SCHEMA_KEY) === SCHEMA) return;
    await idbCache.clear().catch(() => undefined);
    localStorage.setItem(SCHEMA_KEY, SCHEMA);
  } catch {
    /* 隐私模式下读不到 localStorage —— 那就每次都清一遍,也没坏处 */
  }
})();

export interface Cached<T> {
  /** 缓存或新数据;都没有时是 null */
  data: T | null;
  /** true = 眼前这份是缓存,新的还在路上 */
  stale: boolean;
  /** 没有任何数据可显示、正在首次加载 */
  loading: boolean;
  error: string | null;
  /** 手动重拉(比如人点了刷新,或者做完一个操作之后) */
  refresh: () => Promise<void>;
}

export function useCachedApi<T>(path: string | null, opts: { maxAgeMs?: number } = {}): Cached<T> {
  const [data, setData] = useState<T | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * ★ 只认**最后一次**请求的结果。
   *   边打边筛的时候,先发出去的那个请求完全可能比后发的晚回来 ——
   *   然后把新结果盖成旧的。表现是「筛着筛着结果自己跳回去了」:
   *   偶发、不报错,而且数据越多越容易撞上。
   *   (通讯录那一屏踩过同一个坑,见 Contacts.tsx 的 reqSeq。)
   */
  const seq = useRef(0);

  const load = useCallback(
    async (useCache: boolean) => {
      if (!path) return;
      const mine = ++seq.current;
      const key = KEY(path);
      const maxAge = opts.maxAgeMs ?? MAX_AGE_MS;

      if (useCache) {
        const hit = recall<T>(key) ?? (await idbCache.get<Entry<T>>(key).catch(() => undefined));
        if (mine !== seq.current) return;
        if (hit && Date.now() - hit.cachedAt <= maxAge) {
          remember(key, hit);
          setData(hit.payload);
          setStale(true);
        }
      }

      try {
        const fresh = await api<T>(path);
        if (mine !== seq.current) return;
        setData(fresh);
        setStale(false);
        setError(null);
        const entry: Entry<T> = { id: key, cachedAt: Date.now(), payload: fresh };
        remember(key, entry);
        // 写缓存失败(隐私模式下 IndexedDB 可能不可用)不该影响正常显示
        void idbCache.put(entry).catch(() => undefined);
      } catch (e) {
        if (mine !== seq.current) return;
        /*
         * ★ 手上有缓存时,请求失败**不清屏**。
         *   网络抖一下就把满屏数据换成一句红字,是把「暂时看不到最新的」
         *   变成了「什么都看不到」。把错误说出来,数据留着。
         */
        setError(e instanceof Error ? e.message : '加载失败');
      }
    },
    [path, opts.maxAgeMs],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  return {
    data,
    stale,
    loading: data === null && error === null,
    error,
    refresh: () => load(false),
  };
}

/**
 * 退出登录时清掉。
 *
 * ★ 这些缓存里有几千个收件人的姓名、电话、门牌号 ——
 *   留在浏览器里就等于留给下一个在这台电脑上登录的人。
 *   (同一个道理司机端也做了,而管理端的数据面大得多。)
 */
export async function clearCache(): Promise<void> {
  mem.clear();
  await idbCache.clear().catch(() => undefined);
}

/**
 * 直接读 / 写一条缓存。
 *
 * ★ 给**不适合套 hook** 的地方用:订单列表带游标分页,
 *   「先显示上一次的第一页,再去拉新的」这件事只发生在第一页上,
 *   而 hook 管不到 append 那条路径。
 */
export async function readCache<T>(path: string, maxAgeMs = MAX_AGE_MS): Promise<T | null> {
  const key = KEY(path);
  const hit = recall<T>(key) ?? (await idbCache.get<Entry<T>>(key).catch(() => undefined));
  if (!hit || Date.now() - hit.cachedAt > maxAgeMs) return null;
  remember(key, hit);
  return hit.payload;
}

export async function writeCache<T>(path: string, payload: T): Promise<void> {
  const entry: Entry<T> = { id: KEY(path), cachedAt: Date.now(), payload };
  remember(KEY(path), entry);
  await idbCache.put(entry).catch(() => undefined);
}

/**
 * 某一单的状态变了 —— 把本地缓存里那条**就地改掉**。
 *
 * ★ 用户原话:「订单的状态变化都要修改本地缓存的对应数据……
 *   比如结束订单后回到地图上,地图上直接不显示,这样可以减少点错订单」。
 *   最后半句是重点:那个点要是还在,调度员完全可能再点它一次。
 *
 * ★ 这一改**只是让眼前这一刻是对的**,不是真相。
 *   下一次请求回来会整份覆盖 —— 所以这里不必追求算得和服务端一样,
 *   只要别让人看见一个他刚刚亲手改掉的状态。
 *
 * ★ 只认两种形状:订单列表 `{data:[...]}` 和地图 `{pins:[...]}`。
 *   写成「遍历任意 JSON 找 id」更通用,但也更容易在别的接口上
 *   误伤一条同名字段 —— 缓存里躺着错数据是很难查的一类问题。
 *
 * @param terminal 进了终态(已送达/已取消/已退回)。地图只画没结束的单,
 *                 所以终态要从地图缓存里**删掉**,不是改状态。
 */
export async function patchCachedOrder(
  orderId: string,
  patch: { status?: string },
  terminal = false,
): Promise<void> {
  const touched: Array<Entry<unknown>> = [];

  /*
   * ★ 要改的是**落盘的那一份**,不只是内存里那份。
   *
   *   内存是 24 条的 LRU(见 MEM_MAX)。原来这里只遍历它 ——
   *   于是地图那条缓存一旦被挤出去,这一下就什么都没改到:
   *   返回地图时从 IndexedDB 读回来的还是带着那个点的旧数据,
   *   而用户报过的正是这件事(「结束订单后回到地图上直接不显示」)。
   *
   *   更糟的是它**只在多翻几页之后才失效** —— 单子少、页面开得少的时候
   *   一切正常,所以这个洞躺了很久没被发现,直到开发库里的单子多起来。
   *
   * ★ 按前缀从库里扫,再用内存里那份覆盖同 key 的条目:
   *   内存里的可能刚被别处改过、比库里新。
   */
  const stored = await idbCache
    .byPrefix<Entry<unknown>>('admin:/orders')
    .catch(() => [] as Array<Entry<unknown>>);
  const all = new Map<string, Entry<unknown>>();
  for (const e of stored) all.set(e.id, e);
  for (const [key, entry] of mem) all.set(key, entry);

  for (const [key, entry] of all) {
    if (!key.startsWith('admin:/orders')) continue;
    const payload = entry.payload as {
      data?: Array<Record<string, unknown>>;
      pins?: Array<Record<string, unknown>>;
    };
    let changed = false;

    if (Array.isArray(payload.data)) {
      const next = payload.data.map((o) =>
        o['id'] === orderId ? ((changed = true), { ...o, ...patch }) : o,
      );
      if (changed) payload.data = next;
    }
    if (Array.isArray(payload.pins)) {
      const next = terminal
        ? payload.pins.filter((p) => p['id'] !== orderId)
        : payload.pins.map((p) =>
            p['id'] === orderId ? ((changed = true), { ...p, ...patch }) : p,
          );
      if (next.length !== payload.pins.length) changed = true;
      if (changed) payload.pins = next;
    }

    if (changed) {
      // 内存里也要有这一份 —— 下次 readCache 先看内存
      remember(key, entry);
      touched.push(entry);
    }
  }

  // 改完落盘 —— 不落的话刷新一下那条又回来了
  await Promise.all(touched.map((e) => idbCache.put(e).catch(() => undefined)));
}

/**
 * 引用数据(司机 / 区域 / 取货点)的缓存。
 *
 * ★ 和 useCachedApi 分开:这三样是在 App 里一次拉齐、传给各屏的,
 *   不走 hook 那条路。存成一条记录而不是三条 —— 它们总是一起用,
 *   三条各自过期会让界面出现「新司机配旧区域」这种组合。
 *
 * ★ 缓存不到就当没有:这条路径上任何失败都不该影响正常加载。
 */
interface RefsPayload {
  drivers: unknown[];
  zones: unknown[];
  points: unknown[];
}

const REFS_KEY = 'admin:refs';
/** 引用数据变得慢,但也不能无限旧 —— 一天足够跨过一个班次 */
const REFS_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export async function readRefsCache<T extends RefsPayload>(): Promise<T | null> {
  const hit =
    recall<T>(REFS_KEY) ?? (await idbCache.get<Entry<T>>(REFS_KEY).catch(() => undefined));
  if (!hit || Date.now() - hit.cachedAt > REFS_MAX_AGE_MS) return null;
  remember(REFS_KEY, hit);
  return hit.payload;
}

export async function writeRefsCache<T extends RefsPayload>(payload: T): Promise<void> {
  const entry: Entry<T> = { id: REFS_KEY, cachedAt: Date.now(), payload };
  remember(REFS_KEY, entry);
  await idbCache.put(entry).catch(() => undefined);
}
